import { useEffect, useState } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { BETEC_CONTACT } from "./Contacto";
import { trackEvent } from "../lib/analytics";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_ee87a7b5-4234-4e1a-ae00-61e47c3e4eb8/artifacts/czcapx37_LOGO.png";

const NAV_LINKS = [
  { label: "Inicio", href: "#inicio" },
  { label: "Soluciones", href: "#soluciones" },
  { label: "Hardware", href: "#hardware" },
  { label: "Servicios", href: "#servicios" },
  { label: "Nosotros", href: "#nosotros" },
  { label: "Contacto", href: "#contacto" },
];

const WA_LINK = `https://wa.me/${BETEC_CONTACT.whatsapp}?text=${encodeURIComponent(
  "Hola Betec, me gustaría conversar sobre una cotización."
)}`;

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300 ${
        scrolled
          ? "border-b border-slate-200 shadow-[0_2px_24px_-12px_rgba(10,42,78,0.18)]"
          : "border-b border-slate-100"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-[96px] md:h-[112px] flex items-center justify-between">
        {/* Logo - much larger */}
        <a
          href="#inicio"
          data-testid="navbar-logo"
          className="flex items-center gap-2 shrink-0 group"
          aria-label="Betec - Inicio"
        >
          <img
            src={LOGO_URL}
            alt="Betec"
            className="h-16 md:h-20 w-auto object-contain select-none transition-transform duration-300 group-hover:scale-[1.03]"
            draggable={false}
          />
        </a>

        {/* Desktop nav */}
        <nav
          data-testid="navbar-desktop-links"
          className="hidden lg:flex items-center gap-1"
        >
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className="relative px-4 py-2 text-[15px] font-medium text-slate-700 hover:text-[#0A2A4E] transition-colors duration-200 group"
            >
              {link.label}
              <span className="absolute left-4 right-4 -bottom-0.5 h-[2px] bg-[#1FA08A] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
            </a>
          ))}
        </nav>

        {/* Desktop CTAs - WhatsApp + Cotizar */}
        <div className="hidden lg:flex items-center gap-2.5">
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-cta-whatsapp"
            aria-label="Contactar por WhatsApp"
            onClick={() => trackEvent("whatsapp_click", { location: "navbar" })}
            className="group inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white px-4 py-2.5 rounded-sm font-semibold tracking-wide text-sm shadow-md hover:shadow-[0_10px_30px_-10px_rgba(37,211,102,0.55)] hover:-translate-y-0.5 transition-all duration-300"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={2} />
            <span>WhatsApp</span>
          </a>
          <a
            href="#contacto"
            data-testid="navbar-cta-cotizar"
            onClick={() => trackEvent("cta_cotizar_click", { location: "navbar" })}
            className="cta-cotizar inline-flex items-center gap-2.5 bg-[#1FA08A] hover:bg-[#167d6c] text-white px-6 py-2.5 rounded-sm font-semibold tracking-wide text-sm shadow-md hover:shadow-[0_10px_30px_-10px_rgba(31,160,138,0.6)] hover:-translate-y-0.5 transition-all duration-300"
          >
            Cotizar
            <span aria-hidden className="cta-dot" />
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          data-testid="navbar-mobile-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-md text-[#0A2A4E] hover:bg-slate-100 transition-colors"
          aria-label="Abrir menú"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu panel */}
      <div
        data-testid="navbar-mobile-panel"
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-out bg-white border-b border-slate-200 ${
          mobileOpen ? "max-h-[480px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav className="px-6 py-4 flex flex-col">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
              className="py-3 text-[15px] font-medium text-slate-700 hover:text-[#0A2A4E] border-b border-slate-100 last:border-b-0"
            >
              {link.label}
            </a>
          ))}
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileOpen(false)}
            data-testid="navbar-mobile-whatsapp"
            className="mt-4 inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white px-6 py-3 rounded-sm font-semibold text-sm transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </a>
          <a
            href="#contacto"
            onClick={() => setMobileOpen(false)}
            data-testid="navbar-mobile-cta"
            className="cta-cotizar mt-2 mb-2 inline-flex items-center justify-center gap-2.5 bg-[#1FA08A] hover:bg-[#167d6c] text-white px-6 py-3 rounded-sm font-semibold text-sm transition-colors"
          >
            Cotizar
            <span aria-hidden className="cta-dot" />
          </a>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
